// The intake "Advertisement" picker — which advertisement a buyer being
// created (or imported) is attributed to. ONE component for the long form,
// Quick add, paste-to-create and the CSV import, so the four cannot label,
// group or gate the same list four ways.

import { useMemo, useState } from "react";
import type { AdvertisementDto } from "@/api/advertising";
import type { BuyerLeadSource } from "@/api/buyers";
import { Select } from "@/components/ui/Select";
import { FormField } from "@/design-system/components/ui";
import { useAuth } from "@/hooks/useAuth";
import { useAttributableAdvertisements } from "@/hooks/buyers/useAdvertising";
import { sourceForChannel } from "@/lib/advertisingChannelSources";
import { channelLabel } from "@/pages/buyers/advertising/advertisingBits";

/**
 * The advertisement pick and the Source default it implies (Q6), shared by
 * the three create surfaces so the rule has one home.
 *
 * <p>🔴 <b>`sourceTouched` is set by the Source control's OWN onChange only</b>
 * — `setSourceByHand` — and never by the auto-fill. So a Source the specialist
 * chose before picking an advertisement survives the pick, while one the
 * auto-fill wrote is still free to follow the next pick. Clearing the
 * advertisement leaves Source where it is: an auto-filled value is a real
 * answer the specialist has seen, and silently emptying it would make the
 * lead's source vanish on a second thought about the advertisement.</p>
 *
 * <p>The advertisement itself is the attribution (D2 retired the campaign
 * code).</p>
 */
export function useAdvertisementAttribution(setSource: (source: BuyerLeadSource) => void) {
  const [advertisementId, setAdvertisementId] = useState<string | null>(null);
  const [sourceTouched, setSourceTouched] = useState(false);

  return {
    advertisementId,
    pickAdvertisement: (ad: AdvertisementDto | null) => {
      setAdvertisementId(ad?.id ?? null);
      if (ad && !sourceTouched) setSource(sourceForChannel(ad.channel));
    },
    setSourceByHand: (source: BuyerLeadSource) => {
      setSourceTouched(true);
      setSource(source);
    },
  };
}

/** The permission that gates BOTH the control and the read behind it. */
export const ADVERTISING_READ_PERMISSION = "BuyerAdvertising.Read";

/**
 * Q5, verbatim. The advertisement's credit and the buyer's owner are two
 * different facts: the lead's TEAM follows its assignee, the advertisement's
 * credit follows the advertisement. A specialist picking one on a buyer they
 * are about to assign to a colleague on another team needs to be told that
 * here, at the moment they pick, not on a report weeks later.
 */
export const ADVERTISEMENT_CREDIT_NOTE =
  "Credit for this advertisement rolls up under the advertisement's team, even when the buyer is assigned to a consultant on another team.";

export const NO_ADVERTISEMENT_LABEL = "None";

/** A team's advertisements, for one `<optgroup>`. */
interface TeamGroup {
  team: string;
  ads: AdvertisementDto[];
}

/**
 * Group by the resolved team NAME, in the order the server returned the teams,
 * with each team's advertisements in the order they arrived. A missing name
 * (the DTO's `buyerTeamName` is nullable) still gets a group rather than being
 * dropped — an advertisement the caller may attribute to is one they must be
 * able to pick.
 */
export function groupAdvertisementsByTeam(ads: readonly AdvertisementDto[]): TeamGroup[] {
  const groups = new Map<string, TeamGroup>();
  for (const ad of ads) {
    const team = ad.buyerTeamName?.trim() || "Other";
    const existing = groups.get(team);
    if (existing) existing.ads.push(ad);
    else groups.set(team, { team, ads: [ad] });
  }
  return Array.from(groups.values());
}

export interface AdvertisementPickerProps {
  /** The selected advertisement's id; `null` is "none". */
  value: string | null;
  /**
   * The whole DTO rather than the id, so a caller can read the CHANNEL — the
   * intake forms default the lead's Source from it (`sourceForChannel`) — and
   * `null` when the specialist picks none.
   */
  onChange: (ad: AdvertisementDto | null) => void;
  disabled?: boolean;
  /** Forwarded to the `<select>`. `FormField` supplies one when omitted. */
  id?: string;
  /** Grid placement — the forms hand this to `FormField`. */
  className?: string;
}

/**
 * A `<select>` over every advertisement the caller may attribute a lead to,
 * grouped by team, each option "Name · Channel".
 *
 * <p>🔴 <b>Self-gating on `BuyerAdvertising.Read`.</b> Without it this renders
 * NOTHING and fetches nothing — an F2 holds no advertising permission, the
 * endpoint 403s for them, and a form that showed an empty dropdown or logged
 * a failed request on every Quick add would be worse than one with no picker.
 * The four intake forms mount it unconditionally and let it decide; a form
 * that never rendered it simply never sets `advertisementId`.</p>
 *
 * <p>The helper text is the Q5 sentence, always. It is the one thing about
 * this control that is not obvious from the control.</p>
 */
export function AdvertisementPicker({
  value,
  onChange,
  disabled,
  id,
  className,
}: AdvertisementPickerProps) {
  const { hasPermission } = useAuth();
  const canRead = hasPermission(ADVERTISING_READ_PERMISSION);
  const ads = useAttributableAdvertisements({ enabled: canRead });

  const groups = useMemo(() => groupAdvertisementsByTeam(ads.data ?? []), [ads.data]);

  if (!canRead) return null;

  return (
    <FormField label="Advertisement" helperText={ADVERTISEMENT_CREDIT_NOTE} className={className}>
      <Select
        id={id}
        value={value ?? ""}
        disabled={disabled || ads.isPending}
        onChange={(e) => {
          const next = e.target.value;
          onChange(next ? (ads.data?.find((a) => a.id === next) ?? null) : null);
        }}
      >
        <option value="">{NO_ADVERTISEMENT_LABEL}</option>
        {groups.map((g) => (
          <optgroup key={g.team} label={g.team}>
            {g.ads.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name} · {channelLabel(a.channel)}
              </option>
            ))}
          </optgroup>
        ))}
      </Select>
    </FormField>
  );
}
